import type { FunctionComponent } from 'react';
import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';

import { Analytics } from '@trello/atlassian-analytics';
import { dontUpsell, isEmbeddedInAtlassian } from '@trello/browser';
import { DynamicButton, dynamicToken } from '@trello/dynamic-tokens';
import { forTemplate } from '@trello/i18n';
import { DownIcon } from '@trello/nachos/icons/down';
import { Popover, PopoverScreen, usePopover } from '@trello/nachos/popover';
import { Tooltip } from '@trello/nachos/tooltip';
import { BoardHeaderTestIds } from '@trello/test-ids';

import { ResponsiveBoardHeaderContext } from 'app/src/components/BoardHeader/ResponsiveBoardHeaderContext';
import { mergeRefs } from 'app/src/mergeRefs';
import { LazyArrangeViewButtonsPopoverScreen } from './LazyArrangeViewButtonsPopoverScreen';
import type {
  SwitcherView,
  SwitcherView_ViewType,
  ViewOption,
} from './ViewSwitcher';
import { ViewSwitcherButton } from './ViewSwitcherButton';

// eslint-disable-next-line @trello/less-matches-component
import styles from './ViewSwitcher.less';

const format = forTemplate('view_switcher');

interface ViewSwitcherButtonProps {
  idBoard: string;
  currentView: SwitcherView_ViewType | null;
  viewOptions: { [key in SwitcherView_ViewType]: ViewOption };
  switcherViews: SwitcherView[];
  hasViewsFeature: boolean;
  memberCanEditBoard: boolean;
  analyticsContainers?: Parameters<
    typeof Analytics.sendClickedButtonEvent
  >[0]['containers'];
}

export const ViewSwitcherButtons: FunctionComponent<ViewSwitcherButtonProps> = ({
  idBoard,
  currentView,
  viewOptions,
  switcherViews,
  hasViewsFeature,
  memberCanEditBoard,
  analyticsContainers = {},
}) => {
  const { isComponentCollapsed } = useContext(ResponsiveBoardHeaderContext);
  const isCollapsed = isComponentCollapsed('ViewSwitcher');
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  const onShow = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'boardViewSwitcherMoreButton',
      source: 'boardScreen',
      containers: analyticsContainers,
    });
    setIsPopoverOpen(true);
  }, [analyticsContainers]);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  // When the popover is opened, this state is set to the x offset of the caret
  // button. The popover is anchored to a dummy div positioned with that offset
  // so that it doesn't move when enabling or disabling views.
  const [persistentPopoverOffset, setPersistentPopoverOffset] = useState<
    number | undefined
  >(undefined);
  const popoverAnchorOffsetX = useMemo(() => {
    return persistentPopoverOffset ?? buttonRef.current?.offsetLeft ?? 0;
  }, [persistentPopoverOffset]);

  const {
    triggerRef,
    targetRef,
    hide,
    toggle,
    popoverProps,
  } = usePopover<HTMLButtonElement>({
    onShow,
  });

  // Because the popover is anchored to a dummy div instead of the button, this
  // ensures that closing out of the popover returns focus to the button.
  const onPopoverHide = useCallback(() => {
    hide();
    setIsPopoverOpen(false);
  }, [hide]);

  const onToggle = useCallback(() => {
    setPersistentPopoverOffset(buttonRef.current?.offsetLeft);
    toggle();
  }, [toggle]);

  // The buttonRef is used to persist the location the popover was opened from,
  // the triggerRef is supplied from the popover to make sure clicking the
  // button isn't registered as an outsideClick.
  const combinedButtonRefs = mergeRefs(buttonRef, triggerRef);

  const switcherViewButtons = switcherViews.map(({ viewType, enabled }) => {
    if (!enabled) return null;
    if (isEmbeddedInAtlassian() && viewType !== currentView) return null;

    return (
      <ViewSwitcherButton
        key={viewType}
        idBoard={idBoard}
        analyticsContainers={analyticsContainers}
        currentView={currentView}
        viewOption={viewOptions[viewType]}
        viewType={viewType}
        isCollapsed={isCollapsed}
      />
    );
  });

  const collapsedButtonText = format(currentView || 'Board');

  return (
    <div className={styles.switcherButtonsContainer}>
      {!isCollapsed && switcherViewButtons}
      <Tooltip content={format('customize-views')}>
        <DynamicButton
          className={cx(
            styles.overflowIcon,
            isPopoverOpen && styles.highlighted,
          )}
          aria-label={format('customize-views')}
          iconBefore={
            isCollapsed ? (
              React.createElement(viewOptions[currentView || 'Board'].icon, {
                size: 'small',
                color: !isCollapsed
                  ? dynamicToken('dynamic.button.highlighted.text')
                  : dynamicToken('dynamic.text'),
                dangerous_className: styles.viewSwitcherIcon,
              })
            ) : (
              <DownIcon />
            )
          }
          iconAfter={isCollapsed ? <DownIcon /> : undefined}
          ref={combinedButtonRefs}
          onClick={onToggle}
          data-testid={BoardHeaderTestIds.ViewSwitcherButtonMore}
        >
          {isCollapsed && collapsedButtonText}
        </DynamicButton>
      </Tooltip>
      <div
        ref={targetRef}
        className={styles.dummyPopoverTarget}
        style={{
          left: popoverAnchorOffsetX,
        }}
      ></div>
      <Popover
        {...popoverProps}
        onHide={onPopoverHide}
        title={
          hasViewsFeature
            ? memberCanEditBoard
              ? format('add-and-edit-views')
              : format('views')
            : dontUpsell()
            ? format('views')
            : format('upgrade-for-views')
        }
      >
        <PopoverScreen id={0}>
          <LazyArrangeViewButtonsPopoverScreen
            idBoard={idBoard}
            viewOptions={viewOptions}
            switcherViews={switcherViews}
            hasViewsFeature={hasViewsFeature}
            memberCanEditBoard={memberCanEditBoard}
            analyticsContainers={analyticsContainers}
          />
        </PopoverScreen>
      </Popover>
    </div>
  );
};
